import React from "react";
import { motion } from "framer-motion";
import { ParallaxProvider } from "react-scroll-parallax";
import {
  SiReact, SiAzuredevops, SiCisco, SiNextdotjs,
  SiAndroidstudio, SiElectron, SiMysql, SiPostman,
  SiSelenium, SiSpring, SiTailwindcss, SiFirebase,
  SiKalilinux, SiDocker, SiVmware, SiSap, SiOracle, SiFortinet,
  SiAmazonwebservices, SiMicrosoftazure, SiDotnet
} from "react-icons/si";
import { BsAward } from "react-icons/bs";

const fadeInScale = {
  hidden: { opacity: 0, scale: 0.9 },
  show: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.3, ease: "easeOut" }
  }
};

const SkillCard = ({ children, title }) => (
  <motion.div
    initial="hidden"
    whileInView="show"
    viewport={{ once: true }}
    variants={fadeInScale}
    className="bg-zinc-900 rounded-lg p-6 border border-white/10 hover:border-cyan-400/40 transition-all"
    whileHover={{ y: -5, boxShadow: "0 0 20px rgba(34, 211, 238, 0.2)" }}
  >
    <h2 className="text-2xl font-[rekind] mb-4 text-cyan-400">{title}</h2>
    {children}
  </motion.div>
);

const UnifiedPortfolio = () => {
  const certifications = [
    {
      name: "Cisco CCNA",
      icon: <SiCisco className="text-4xl" />,
      date: "2022"
    },
    {
      name: "Cisco CCNP",
      icon: <SiCisco className="text-4xl" />,
      date: "2023 "
    },
   
    {
      name: "AWS Solutions Architect",
      icon: <SiAmazonwebservices className="text-4xl" />,
      date: "2024"
    },
    {
      name: "Azure Administrator",
      icon: <SiMicrosoftazure className="text-4xl" />,
      date: "2024"
    },
    
  ];

  const technicalSkills = [
    { category: "Frontend", skills: ["React", "Tailwind CSS", "AngularJS"] },
    { category: "Backend", skills: ["Node.js", "Spring Boot", "ASP.NET"] },
    { category: "Cloud", skills: ["AWS", "Azure", "Firebase"] },
    { category: "Network", skills: ["Cisco", "FortiGate", "VMware"] }
  ];

  const proficiencyData = [
    { skill: "JavaScript", level: 80 },
    { skill: "C#", level: 85 },
    { skill: "Java", level: 80 },
    { skill: "Python", level: 82 },
    { skill: "C++", level: 73 }
  ];

  return (
    <ParallaxProvider>
      <div className="min-h-screen bg-black text-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            <h1 className="sm:text-3xl md:text-4xl items-center gap-8 flex text-5xl font-[rekind] text-cyan-400">
            <BsAward/> Skills
          </h1>
          <div className="h-1 w-40 bg-cyan-400 rounded-full mb-8" />

            {/* Technical Skills Matrix */}
            <SkillCard title="Technical Skills">
              <div className="grid grid-cols-2 gap-4">
                {technicalSkills.map((category) => (
                  <div key={category.category} className="space-y-2">
                    <h3 className="text-lg font-semibold text-cyan-400/80">
                      {category.category}
                    </h3>
                    {category.skills.map((skill) => (
                      <div
                        key={skill}
                        className="text-sm bg-zinc-800/50 px-3 py-1 rounded-full text-gray-300"
                      >
                        {skill}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </SkillCard>
            {/* Certifications Section */}
            <SkillCard title="Certifications">
              <div className="space-y-4">
                {certifications.map((cert) => (
                  <motion.div
                    key={cert.name}
                    whileHover={{ x: 10 }}
                    className="flex items-center space-x-4 p-3 rounded-lg bg-zinc-800/50 border border-cyan-400/20"
                  >
                    <div className="text-cyan-400">{cert.icon}</div>
                    <div>
                      <h3 className="font-semibold text-lg">{cert.name}</h3>
                      <p className="text-sm text-gray-400">{cert.date}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </SkillCard>

            {/* Language Proficiency */}
            <SkillCard title="Language Proficiency">
              <div className="space-y-4">
                {proficiencyData.map((item) => (
                  <div key={item.skill}>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm text-gray-300">{item.skill}</span>
                      <span className="text-sm text-cyan-400">{item.level}%</span>
                    </div>
                    <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: `${item.level}%` }}
                        transition={{ duration: 1 }}
                        className="h-full bg-cyan-400/80"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </SkillCard>

            {/* Tools & Technologies */}
            <motion.div
              className="md:col-span-2 lg:col-span-3"
              variants={fadeInScale}
              initial="hidden"
              whileInView="show"
            >
              <SkillCard title="Tools & Technologies">
                <div className="grid grid-cols-8 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-9 gap-4">
                  {[SiReact, SiAzuredevops, SiCisco, SiNextdotjs, SiAndroidstudio, 
                    SiElectron, SiMysql, SiPostman, SiSelenium, SiSpring, 
                    SiTailwindcss, SiFirebase, SiKalilinux, SiDocker, SiDotnet, SiVmware, 
                    SiSap, SiOracle, SiFortinet].map((Icon, index) => (
                    <motion.div
                      key={index}
                      whileHover={{ scale: 1.2, color: "rgb(34, 211, 238)" }}
                      className="flex flex-col items-center justify-center p-2"
                    >
                      <Icon className="text-4xl sm:text-xl md:text-2xl text-gray-400 hover:text-cyan-400 transition-colors" />
                    </motion.div>
                  ))}
                </div>
              </SkillCard>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </ParallaxProvider>
  );
};

export default UnifiedPortfolio;